import React, { createContext, useEffect, useState } from 'react';
import cloneDeep from 'lodash.clonedeep';
import * as PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { collection, doc, onSnapshot, setDoc, updateDoc, deleteDoc, addDoc, getDoc, getDocs, getFirestore } from 'firebase/firestore';
import { useFirebase } from '../firebase/firebase-provider';
import { useDataStore } from './datastore-provider';
import { useHome } from './home-provider';
import { useAuth } from './auth-provider';

interface RecipeManagerContextType {
    userRecipes: any[] | null;
    homeRecipes: any[];
    addRecipe: (recipe: any) => Promise<unknown>;
    removeRecipe: (recipeId: any) => void;
    getHomeRecipe: (recipeId: any) => any;
    getUserRecipe: (recipeId: any) => any;
    saveRecipe: (recipe: any) => void;
    getAllFriendsRecipes: (friendId: any) => Promise<unknown>;
    getFriendsRecipe: (friendId: any, recipeId: any) => Promise<unknown>;
}

const COLLECTION_NAME = 'recipes';
const RecipeManager = createContext<RecipeManagerContextType | undefined>(undefined);

export const useRecipeManager = () => {
    const context = React.useContext(RecipeManager);
    if (context === undefined) {
        throw new Error('useRecipeManager must be used within a RecipeManagerProvider');
    }
    return context;
};

const RECIPE_DEFAULTS = {
    labels: [],
    serves: 4,
    ingredients: [],
    method: [],
    timesAddedAsMeal: 0,
};

RecipeManagerProvider.propTypes = {
    children: PropTypes.element.isRequired,
};
export function RecipeManagerProvider(props: any) {
    const [userRecipes, setUserRecipes] = useState(null);
    const [homeRecipes, setHomeRecipes] = useState([]);
    const app = useFirebase();
    const { currentUser } = useAuth();
    const { userDb, getUserStore } = useDataStore();
    const { home } = useHome();
    const { enqueueSnackbar } = useSnackbar();

    useEffect(loadUserRecipes, [userDb]);
    useEffect(loadHomeRecipes, [home]);

    function loadUserRecipes() {
        if (userDb) {
            const recipesCollection = collection(userDb, COLLECTION_NAME);

            const unsubscribe = onSnapshot(
                recipesCollection,
                (querySnapshot: any) => {
                    if (!querySnapshot.docs || !querySnapshot.docs.length) {
                        setUserRecipes([]);
                    } else {
                        setUserRecipes(querySnapshot.docs.map((doc: any) => loadRecipe(doc.data(), doc.id)));
                    }
                },
                (error: any) => {
                    console.debug(error);
                    setUserRecipes(userRecipes || []);
                    enqueueSnackbar('Oops! Something went wrong', { variant: 'error' });
                }
            );
            return unsubscribe;
        } else {
            setUserRecipes(null);
        }
    }

    function loadHomeRecipes() {
        if (home && home.id) {
            const homeRecipePromises: any = [];
            home.members.filter((m: any) => m.id !== currentUser.uid)
                .forEach((m: any) => homeRecipePromises.push(() => getAllFriendsRecipes(m.id)));
            homeRecipePromises.reduce(async (previousPromise, nextAsyncFunction) => {
                await previousPromise;
                const memberRecipes = await nextAsyncFunction();
                setHomeRecipes([...homeRecipes, ...memberRecipes]);
            }, Promise.resolve());
        }
    }

    function getAllFriendsRecipes(friendId: any) {
        const store = getUserStore(friendId);
        if (store) {
            return new Promise(((resolve) => {
                const recipesCollection = collection(store, COLLECTION_NAME);
                getDocs(recipesCollection)
                    .then((querySnapshot: any) => {
                        if (!querySnapshot.docs || !querySnapshot.docs.length) {
                            resolve([]);
                        } else {
                            resolve(querySnapshot.docs.map((doc: any) => loadRecipe(doc.data(), doc.id, friendId)));
                        }
                    });
            }));
        }
        return Promise.reject(new Error('Failed to retrieve friends userRecipes'));
    }

    function getFriendsRecipe(friendId: any, recipeId: any) {
        const store = getUserStore(friendId);
        if (store) {
            return new Promise(((resolve) => {
                const recipeDoc = doc(store, COLLECTION_NAME, recipeId);
                getDoc(recipeDoc)
                    .then((docSnap: any) => {
                        if (!docSnap.exists()) {
                            resolve(null);
                            enqueueSnackbar('Oops! Something went wrong', { variant: 'error' });
                        } else {
                            resolve(loadRecipe(docSnap.data(), docSnap.id));
                        }
                    });
            }));
        }
        return Promise.reject(new Error('Failed to retrieve recipe'));
    }
    /**
     * Load a recipe
     * Do any upgrading required between versions of the data
     * @param recipe The recipe to upgrade
     * @param id  The recipe id
     * @param sharedFrom User that shared this recipe, null if owned by the logged in user
     */
    function loadRecipe(recipe: any, id: any, sharedFrom = null) {
        let loadedRecipe = { method: [], ...recipe, recipeId: id, sharedFrom };
        if (recipe.method && !Array.isArray(recipe.method)) {
            loadedRecipe = { ...loadedRecipe, method: [], notes: recipe.method };
        }
        return loadedRecipe;
    }

    /**
     * Add a single recipe
     * @param recipe Recipe data as an object
     * @return The id of the newly added recipe
     */
    function addRecipe(recipe: any) {
        return new Promise((resolve, reject) => {
            const newRecipe = { ...cloneDeep(RECIPE_DEFAULTS), ...recipe };
            if (userDb) {
                const recipesCollection = collection(userDb, COLLECTION_NAME);
                addDoc(recipesCollection, newRecipe)
                    .then((docRef: any) => {
                        enqueueSnackbar('Recipe Added', { variant: 'success' });
                        resolve(docRef.id);
                    })
                    .catch((error: any) => {
                        console.debug(error);
                        enqueueSnackbar('Oops! Something went wrong', { variant: 'error' });
                        reject(error);
                    });
            }
        });
    }

    /**
     * Save a recipe
     * @param recipe The recipe to save
     */
    function saveRecipe(recipe: any) {
        if (userDb) {
            const recipeDoc = doc(userDb, COLLECTION_NAME, recipe.recipeId);
            updateDoc(recipeDoc, recipe)
                .then(() => enqueueSnackbar('Recipe Saved', { variant: 'success' }))
                .catch((error: any) => {
                    console.debug(error);
                    enqueueSnackbar('Oops! Something went wrong', { variant: 'error' });
                });
        }
    }

    /**
     * Remove a single recipe
     * @param recipeId The id of the recipe to remove
     */
    function removeRecipe(recipeId: any) {
        if (userDb) {
            const undoRemove = () => {
                const deleted = userRecipes.find((r: any) => r.recipeId === recipeId);
                const recipeDoc = doc(userDb, COLLECTION_NAME, recipeId);
                setDoc(recipeDoc, deleted)
                    .then(() => enqueueSnackbar('Action Undone', { variant: 'success' }));
            };
            const recipeDoc = doc(userDb, COLLECTION_NAME, recipeId);
            deleteDoc(recipeDoc)
                .then(() => enqueueSnackbar('Recipe Deleted', { variant: 'success', action: (key) => (
                    <button onClick={() => { undoRemove(); }}>UNDO</button>
                ) }))
                .catch((error: any) => {
                    console.debug(error);
                    enqueueSnackbar('Oops! Something went wrong', { variant: 'error' });
                });
        }
    }

    function getHomeRecipe(recipeId: any) { return getRecipe(recipeId, homeRecipes); }
    function getUserRecipe(recipeId: any) { return getRecipe(recipeId, userRecipes); }
    function getRecipe(recipeId: any, theRecipes: any) {
        if (!recipeId || !theRecipes) return null;
        const recipe = theRecipes.find((r: any) => r.recipeId === recipeId);
        return cloneDeep(recipe);
    }

    return (
        <RecipeManager.Provider value={{ userRecipes, homeRecipes, addRecipe, removeRecipe, getHomeRecipe, getUserRecipe, saveRecipe, getAllFriendsRecipes, getFriendsRecipe }}>
            { props.children }
        </RecipeManager.Provider>
    );
}
